import { useAction, useAtom } from '@reatom/npm-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { SearchInput } from '@blade/ui-kit'
import { reatomSearchPair } from '@root/features/services/search-pair'
import { recentSearchesModelAtom } from '@root/features/user/recent-searches'
import { Drawer } from '@root/shared/ui-kit'

import { IdleSearchContent } from './idle-search-content'
import { SearchResults } from './search-results'

import type { Pair } from '@root/entities/pair'
import type { DrawerControlProps } from '@root/shared/ui-kit'

type RecentPairsDrawerProps = {
  onPairSelected?: (pair: Pair.PairMinimal) => void
} & DrawerControlProps

export const SearchDrawer: React.FC<RecentPairsDrawerProps> = ({
  open,
  onOpenChange,
  onPairSelected,
}) => {
  // eslint-disable-next-line @reatom/unit-naming-rule
  const model = useMemo(() => reatomSearchPair(), [])
  const { t } = useTranslation()

  const [searchQuery] = useAtom(model.searchQueryAtom)
  const [searchState] = useAtom(model.searchStateAtom)

  // Keep on this level to prevent connect/disconnect on switching drawer snap
  const [recentSearchesModel] = useAtom(recentSearchesModelAtom)
  const [recentSearchesState] = useAtom(recentSearchesModel.recentSearchesAtom)

  const setQuery = useAction(model.setQueryAction)

  const renderContent = () => {
    if (AsyncStateN.isNotInitiated(searchState)) {
      return (
        <IdleSearchContent
          recentSearchesState={recentSearchesState}
          onPairSelected={onPairSelected}
        />
      )
    }

    return <SearchResults onPairSelected={onPairSelected} state={searchState} />
  }

  return (
    <Root title={t('common.search')} open={open} onOpenChange={onOpenChange}>
      <Content>
        <Input
          size="h40"
          placeholder={t('currency.search_token_or_address')}
          value={searchQuery}
          onChange={(event) => setQuery(event.target.value)}
        />
        {renderContent()}
      </Content>
    </Root>
  )
}

const Root = styled(Drawer)`
  height: var(--max-drawer-height);
`

const Content = styled.div`
  display: grid;
  grid-auto-rows: auto minmax(0, 1fr);
`

const Input = styled(SearchInput)`
  margin-bottom: 12px;
  margin-inline: 12px;
`
